export const personalInfo = {
  name: "Geo Analytics",
  title: "Data Scientist",
  tagline: "Au croisement de la géotechnique et de la data science",
  subtitle: "Transformer les données géotechniques et de mécanique des roches en informations exploitables grâce à l'analyse avancée, l'apprentissage automatique et la modélisation statistique.",
  email: "contact@geo-analytics.fr",
  location: "Bordeaux, France",
  phone: "+33 6 13 08 57 95",
  linkedin: "https://linkedin.com/in/example",
  github: "https://github.com/example",
  researchgate: "https://researchgate.net/profile/example",
  orcid: "https://orcid.org/0000-0000-0000-0000",
  bio: "Avec 5 ans d'expérience en data science, j'accompagne les équipes de recherche et les bureaux d'études dans l'analyse de données géotechniques complexes. Mon travail couvre l'ensemble de la chaîne, des essais en laboratoire, colonnes résonantes, triaxiaux cycliques, jusqu'à l'analyse de systèmes de monitoring de versants et d'ouvrages géotechniques. Mes travaux ont été publiés dans des revues scientifiques internationales, notamment Results in Engineering et Geotechnics. Ils portent sur la prédiction de propriétés dynamiques des sols par machine learning, l'évaluation de la qualité de bases de données géotechniques, et la modélisation de déplacements par deep learning sur des données de monitoring long-terme. Je collabore avec des institutions publiques de recherche, des organismes techniques et des laboratoires universitaires en France et en Europe.",
  bioExtended: ""
};

export const expertise = [
  {
    id: 1,
    title: "Analyse de données géotechniques",
    description: "Traitement et interprétation de données d'essais en laboratoire — colonnes résonantes, triaxiaux cycliques, essais œdométriques — et de campagnes de monitoring terrain. Des données brutes aux résultats exploitables, avec une attention particulière à la qualité et à la cohérence des mesures.",
    icon: "Layers"
  },
  {
    id: 2,
    title: "Surveillance de sites et détection d'anomalies",
    description: "Analyse de données de surveillance géotechnique et géodésique en continu, avec détection automatique de changements de comportement et systèmes d'alerte précoce. Applicable aussi bien aux ouvrages géotechniques qu'aux instabilités naturelles de versants.",
    icon: "Mountain"
  },
  {
    id: 3,
    title: "Modélisation prédictive par IA",
    description: "Développement de modèles de machine learning et deep learning adaptés aux problématiques géoscientifiques : séries temporelles, régression, classification et quantification d'incertitude. Les modèles sont conçus pour être interprétables et directement utilisables dans un contexte opérationnel.",
    icon: "BrainCircuit"
  },
  {
    id: 4,
    title: "Ingénierie des données et automatisation",
    description: "Conception de pipelines de bout en bout pour laboratoires et systèmes de monitoring terrain : ingestion, nettoyage, validation et génération automatisée de rapports. L'objectif est de réduire le temps entre la mesure et la décision.",
    icon: "Database"
  },
  {
    id: 5,
    title: "Evaluation et valorisation de bases de données",
    description: "Méthodes quantitatives pour harmoniser, évaluer la qualité et sélectionner des bases de données géotechniques hétérogènes en vue de la modélisation. Une étape souvent négligée mais déterminante pour la fiabilité des modèles prédictifs.",
    icon: "MapPin"
  },
  {
    id: 6,
    title: "Visualisation scientifique et restitution",
    description: "Développement d'outils de visualisation sur mesure pour données géoscientifiques : courbes dynamiques, analyses spatiales, nuages de points LiDAR et tableaux de bord de monitoring. La restitution fait partie intégrante de la valeur produite.",
    icon: "BarChart3"
  }
];

export const technologies = [
  { name: "Python", category: "Language" },
  { name: "R", category: "Language" },
  { name: "SQL", category: "Language" },
  { name: "TensorFlow", category: "ML/AI" },
  { name: "scikit-learn", category: "ML/AI" },
  { name: "PyTorch", category: "ML/AI" },
  { name: "Pandas", category: "Data" },
  { name: "NumPy", category: "Data" },
  { name: "FLAC3D", category: "Geotechnical" },
  { name: "PLAXIS", category: "Geotechnical" },
  { name: "GIS/QGIS", category: "Geotechnical" },
  { name: "ParaView", category: "Visualization" },
  { name: "Matplotlib", category: "Visualization" },
  { name: "Plotly", category: "Visualization" },
  { name: "Docker", category: "DevOps" },
  { name: "PostgreSQL", category: "Database" }
];

export const publications = [
  {
    id: 1,
    title: "Exploring Database Quality Through Shapley Values: Application to Dynamic Soil Parameters Databases",
    journal: "Geotechnics",
    year: 2025,
    authors: "Borderon, J., Dufour, N., & Régnier, J.",
    doi: "https://doi.org/10.3390/geotechnics5030061",
    abstract: "Geotechnical engineering faces challenges related to data, especially the ones related to dynamic soil behavior (i.e., shear modulus reduction and damping ratio curves with strain), with only a few datasets in open-access format and a slow transition to a more data-driven method. This lack of data, combined with variations in data collection methods, makes it difficult to build accurate predictive models. These challenges arose while developing a model to predict the shear modulus curves, an important soil property to better understand seismic hazard from three different databases. Combining multiple databases can sometimes degrade model performance. To address this, a novel approach in geotechnics based on Shapley values computed from an XGBoostRegressor model is introduced. This game–theoretic method quantifies each database’s marginal contribution to the model’s R2 across all possible combinations, making it possible to identify which databases contribute most to improving performance. As the number of available databases continues to grow, this method will become increasingly useful. For shear modulus reduction curves, two out of three databases explored have Shapley values of 0.341 and 0.339, while the last one reaches only a value of 0.320. This suggests that the first two databases contribute more to the model’s performance.",
    type: "journal",
    tags: ["Machine Learning", "Shapley values", "Database"]
  },
  {
    id: 2,
    title: "Fatigue behavior of lime- and/or cement-treated soils: A database analysis and empirical modeling approach",
    journal: "Computers & Geotechnics",
    year: 2025,
    authors: "Krzewinski, V., Dufour, N., Chevalier, C., Borderon, J., Gasc-Barbier, M.",
    doi: "https://doi.org/10.1016/j.rineng.2025.107682",
    abstract: "Soil improvement using lime and/or hydraulic binders is a well-established technique in geotechnical engineering, particularly in road and railway infrastructures. However, treated soils are subjected to cyclic loading, leading to a fatigue phenomenon that progressively degrade their mechanical properties. This study is based on a database of 410 laboratory tests extracted from 12 published articles (1984–2025), covering various soil types and experimental conditions. A detailed analysis of the influence of parameters on fatigue is conducted using correlation matrices, Principal Component Analysis, and Wöhler diagrams. The results highlight the significance of curing duration, with long-term curing (>120 days) giving more consistent fatigue behavior. The influence of cement classification used to treat soil and soil composition is also demonstrated, showing that CEM II treated specimens exhibit higher fatigue resistance. Finally, an empirical fatigue model is developed to predict the fatigue resistance of treated soils.",
    type: "journal",
    tags: ["Database", "Soil Improvement", "Statistics"]
  },
  {
    id: 3,
    title: "Predicting Fatigue Failure in Treated Soils Using Gradient Boosting Classification",
    journal: "Proceedings of the 3FOMLIG (Third Workshop on the Future of Machine Learning in Geotechnics)",
    year: 2025,
    authors: "Borderon, J., Dufour, N.",
    doi: "https://fomlig2025.com/wp-content/uploads/2025/10/PS11-ID72.pdf",
    abstract: "This paper explores the use of machine learning, specifically XGBoost, to predict fatigue failure in treated soils (with lime or cement) under cyclic loading. Using a dataset of 410 tests, the authors classify the number of cycles to failure into three categories (low, high, very high fatigue) and achieve solid performance, with F1-scores ranging from 0.75 to 0.85. The results show that the most influential factors are the stress ratio and the loading frequency, which outweigh material-related parameters such as cement content or curing time. However, the study also highlights important limitations, including the relatively small and heterogeneous dataset, meaning the findings should be interpreted cautiously despite the promising potential of these methods.",
    type: "conference",
    tags: ["Deep Learning", "Point Cloud", "Rock Mass Characterization"]
  },
  {
    id: 4,
    title: "Prediction of Shear Modulus Reduction and Damping Curves for Clayey Soils using Machine Learning",
    journal: "Proceedings of the ISGSR 2025 (9th International Symposiumon Geotechnical Safety and Risk)",
    year: 2025,
    authors: "Borderon, J., Régnier, J., Dufour, N.",
    doi: "10.1680/jgeot.2023.00045",
    abstract: "An investigation into the spatial variability of shear strength parameters using geostatistical methods, with implications for probabilistic slope stability analysis...",
    type: "conference",
    tags: ["Geostatistics", "Slope Stability", "Spatial Variability"]
  },
  {
    id: 5,
    title: "Comportement de fatigue des sols traités à la chaux et/ou aux liants hydrauliques",
    journal: "Academic Journal of Civil Engineering",
    year: 2025,
    authors: "Dupont, A., Garcia, R., & Fontaine, M.",
    doi: "https://doi.org/10.26168/ajce.43.1.10",
    abstract: "We develop an ensemble learning approach for real-time anomaly detection in TBM operational parameters, enabling early warning of adverse geological conditions...",
    type: "journal",
    tags: ["Anomaly Detection", "TBM", "Real-Time Monitoring"]
  }
];

export const conferences = [
  {
    id: 1,
    name: "3FOMLIG (Third Workshop on the Future of Machine Learning in Geotechnics)",
    location: "Florence, Italie",
    date: "Octobre 2025",
    year: 2025,
    role: "Présentateur",
    topic: "Predicting Fatigue Failure in Treated Soils Using Gradient Boosting Classification",
    description: ""
  },
  {
    id: 2,
    name: "ISGSR 2025 (9th International Symposiumon Geotechnical Safety and Risk)",
    location: "Oslo, Norvège",
    date: "Août 2025",
    year: 2025,
    role: "Présentateur",
    topic: "Prediction of Shear Modulus Reduction and Damping Curves for Clayey Soils using Machine Learning",
    description: ""
  },
  {
    id: 3,
    name: "DTE & AICOMAS",
    location: "Paris, France",
    date: "Janvier 2025",
    year: 2025,
    role: "Présentateur",
    topic: "Prediction of shear modulus for clayey soils using ensemble machine learning method",
    description: ""
  }
];

export const stats = [
  { label: "", value: "" },
  { label: "", value: "" },
  { label: "", value: "" },
  { label: "", value: "" }
];

export const services = [
  {
    id: 1,
    title: "Analyse de données",
    description: "Analyse experte de jeux de données géotechniques et de mécanique des roches. De l'analyse exploratoire à la modélisation statistique avancée, je vous aide à découvrir des tendances et prendre des décisions basées sur les données.",
    icon: "LineChart"
  },
  {
    id: 2,
    title: "Modélisation prédictive",
    description: "Modèles de machine learning sur mesure pour la prédiction de paramètres géotechniques, le comportement des sols et les propriétés des massifs rocheux. Solutions adaptées et validées selon votre contexte géologique.",
    icon: "TrendingUp"
  },
  {
    id: 3,
    title: "Pipelines de rapports automatisés",
    description: "Workflows automatisés de bout en bout transformant les données brutes de laboratoire et de terrain en rapports et visualisations prêts à publier, économisant des semaines de traitement manuel.",
    icon: "FileText"
  },
  {
    id: 4,
    title: "Collaboration de recherche",
    description: "Projets de recherche conjoints avec institutions académiques et partenaires industriels. Co-rédaction de publications et développement de méthodologies novatrices à la frontière de la data science géotechnique.",
    icon: "Users"
  }
];
